import { useMemo, useRef, useState } from 'react';
import { Button, Card } from '@kss/ui';
import { Download, ExternalLink, Info, QrCode } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { encodeId } from '../../utils/idCodec';
import {
  QR_COLOR_OPTIONS,
  contrastRatio,
  isQrContrastSafe,
  useQrColor,
} from '../../utils/qrColor';

/**
 * ProductQrCard — dynamic per-product QR code shown on the panel's
 * product edit AND create page.
 *
 * The QR encodes a LIVE URL for this product — either the customer-
 * facing storefront page (default) or the admin-panel quick-edit page,
 * selected via the segmented toggle at the top of the card. Nothing
 * is persisted server-side by default: the value is recomputed at
 * render time from `productId` plus the current origin, so if the
 * tenant swaps domains or the product id changes the QR updates on
 * the next paint.
 *
 * When `customQrImageUrl` is supplied, the card renders the uploaded
 * image INSTEAD of the generated QR — this lets merchants use a
 * branded / stylised QR they generated externally. Uploaded image
 * takes precedence over the auto-generated canvas AND disables the
 * Storefront / Panel toggle (the upload has its own baked-in target).
 *
 * URL construction, keyed on the selected `qrMode`:
 *   'storefront' → `${storefrontOrigin()}/shop/${encodeId(productId)}`
 *   'panel'      → `${window.location.origin}/panel/products/${encodeId(productId)}`
 *
 * Origin resolution order for storefront-mode QRs:
 *   1. window.__KSS_CONFIG__.storefrontOrigin   ← runtime-config override
 *   2. window.location.origin                    ← same-origin fallback
 *
 * The runtime-config override is REQUIRED when the panel and storefront
 * live on different origins — the two big cases are:
 *   - local dev (panel on localhost:5194, storefront on localhost:5183)
 *   - split-domain prod (panel.acme.com vs shop.acme.com)
 * Without it the QR encodes the panel's origin, which has no `/shop/:id`
 * route → scan lands on 404 or the panel login screen.
 *
 * Admin-mode QRs always use `window.location.origin` — the panel is the
 * one the admin is currently signed into, so there's nothing to override.
 *
 * `encodeId` matches the base64url-obfuscation used by `/shop/:id` and
 * `/panel/products/:id` links throughout the app.
 *
 * Why the toggle? SSO(2) Issue 2 surfaced two overlapping use-cases:
 *   1. Physical shelf labels the CUSTOMER scans on their phone → they
 *      should land on the storefront page ready to buy.
 *   2. Warehouse / staff labels an ADMIN scans from a phone → they
 *      should land on the panel edit page to update stock, price, etc.
 * A single mode couldn't cover both without wrong-audience redirects,
 * so the admin picks the mode when generating the QR.
 *
 * Download — for the generated QR uses `canvas.toDataURL('image/png')`
 * → anchor-click (no server round-trip). For an uploaded custom image
 * we anchor-click the hosted URL directly with the `download` attribute
 * so the browser saves rather than navigates. If cross-origin blocks
 * that hint, the click falls through to opening in a new tab.
 *
 * Note: `productId` is expected to be non-empty in normal use — the
 * caller pre-mints a UUID in create mode so the QR is available BEFORE
 * the first save. The empty-productId branch is retained purely as a
 * defensive fallback (e.g. render before state hydrates).
 */
export interface ProductQrCardProps {
  productId: string | null | undefined;
  productName?: string;
  sku?: string;
  /**
   * Optional admin-uploaded QR image URL. When present the card renders
   * this image instead of the generated QR; when empty / undefined the
   * card falls back to the auto-generated storefront-URL QR.
   */
  customQrImageUrl?: string | null;
}

type QrMode = 'storefront' | 'panel';

export default function ProductQrCard({
  productId,
  productName,
  sku,
  customQrImageUrl,
}: ProductQrCardProps) {
  const canvasWrapRef = useRef<HTMLDivElement>(null);
  const hasCustom = !!(customQrImageUrl && customQrImageUrl.trim());

  /* Segmented mode toggle — defaults to `storefront` so the common
     "print shelf labels for customers" flow needs zero extra clicks.
     Admins pivoting to warehouse labels flip once and stay. Local
     state only: no persistence, since a merchant may want different
     modes on different products in the same session. */
  const [qrMode, setQrMode] = useState<QrMode>('storefront');

  /* QR foreground colour — 15 accent themes + black + "match theme".
     Persisted (see `useQrColor`) so the bulk A4 sheet reachable from the
     product grid prints in the same colour the admin picked here. */
  const { pref: colorPref, color: qrFgColor, setPref: setColorPref } = useQrColor();
  const contrastSafe = isQrContrastSafe(qrFgColor);

  const qrUrl = useMemo(() => {
    if (!productId) return '';
    const panelOrigin =
      typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
    // Runtime-config override so split-origin deployments and localhost
    // dev can point the QR at the actual storefront host. Trim trailing
    // slash for the '${origin}/shop/...' template.
    const configured =
      typeof window !== 'undefined'
        ? (window as unknown as { __KSS_CONFIG__?: { storefrontOrigin?: string } }).__KSS_CONFIG__
            ?.storefrontOrigin
        : undefined;
    const storefrontOrigin = (configured?.trim() || panelOrigin).replace(/\/+$/, '');
    const encoded = encodeId(productId);
    return qrMode === 'panel'
      ? `${panelOrigin}/panel/products/${encoded}`
      : `${storefrontOrigin}/shop/${encoded}`;
  }, [productId, qrMode]);

  // Dev / misconfig hint — surface a visible warning when the storefront
  // URL points at localhost, since that QR won't resolve from a mobile
  // scanning it in the real world. Only shown in storefront mode
  // (admin-mode always uses the panel's own origin, which the admin is
  // clearly already able to reach).
  const localhostWarning = useMemo(() => {
    if (qrMode !== 'storefront' || !qrUrl) return null;
    try {
      const host = new URL(qrUrl).hostname;
      if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local')) {
        return `This QR points at ${host}. A mobile phone won't be able to reach it — set window.__KSS_CONFIG__.storefrontOrigin in runtime-config.js to the public storefront URL before printing.`;
      }
    } catch {
      /* invalid URL — ignore */
    }
    return null;
  }, [qrMode, qrUrl]);

  // File tag captures both the target audience and the product identity
  // so admins can distinguish shelf QRs from staff QRs in Downloads.
  const modeTag = hasCustom ? 'custom' : qrMode === 'panel' ? 'admin' : 'shop';
  const fileBase = (sku || productId || 'product').toString().replace(/[^a-z0-9_-]+/gi, '-');

  const handleDownload = () => {
    // Uploaded custom image — anchor-click the hosted URL. Browser
    // honours `download` on same-origin / CORS-permitted URLs, and
    // falls back to opening the tab otherwise (still useful).
    if (hasCustom) {
      const a = document.createElement('a');
      a.href = customQrImageUrl!;
      a.download = `qr-${modeTag}-${fileBase}.png`;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }
    // Generated QR — grab the <canvas> qrcode.react rendered and
    // convert to a data-URL. Empty guard covers the pre-mount case.
    const canvas = canvasWrapRef.current?.querySelector('canvas');
    if (!canvas) return;
    try {
      const dataUrl = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `qr-${modeTag}-${fileBase}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      /* toDataURL can throw on tainted canvases — non-critical, swallow. */
    }
  };

  if (!productId) {
    // Defensive — should never happen once the caller pre-mints the id
    // on mount. Keep the graceful hint just in case a caller forgets.
    return (
      <Card>
        <div className="flex items-center gap-2 border-b border-border pb-2">
          <QrCode className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold text-content-primary">Product QR code</h3>
        </div>
        <p className="mt-3 text-xs text-content-muted">
          QR code is unavailable until a product id is assigned.
        </p>
      </Card>
    );
  }

  const isPanelMode = qrMode === 'panel';
  const urlLabel = hasCustom
    ? 'Uploaded QR'
    : isPanelMode
    ? 'Admin panel URL'
    : 'Storefront URL';
  const helperText = hasCustom
    ? 'Showing your uploaded QR image. Clear the custom upload below to fall back to the auto-generated code.'
    : isPanelMode
    ? 'Scanning this code opens the admin product edit page — pick this for warehouse and staff labels.'
    : 'Scanning this code opens the live storefront page — safe to print on customer-facing labels.';

  return (
    <Card>
      <div className="flex items-center justify-between border-b border-border pb-2">
        <div className="flex items-center gap-2">
          <QrCode className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold text-content-primary">Product QR code</h3>
          <span
            className="inline-flex items-center text-content-muted"
            title="The QR encodes this product's storefront or admin URL. Print or share to let customers or staff quickly reach the product page."
          >
            <Info className="h-3.5 w-3.5" />
          </span>
        </div>
        <a
          href={qrUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          title={`Open ${urlLabel.toLowerCase()} in a new tab`}
        >
          Open <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      {/* Mode picker — controls which URL the QR encodes. Segmented-
          button pattern (not a dropdown) keeps both options visible so
          admins can compare and pick the right label per print run.
          Disabled when a custom upload is in effect since the upload
          has its own baked-in target. */}
      {!hasCustom && (
        <div
          role="tablist"
          aria-label="QR target"
          className="mt-3 inline-flex overflow-hidden rounded-md border border-border text-xs"
        >
          <button
            type="button"
            role="tab"
            aria-selected={!isPanelMode}
            onClick={() => setQrMode('storefront')}
            className={`px-3 py-1.5 font-medium transition-colors ${
              !isPanelMode
                ? 'bg-primary text-white'
                : 'bg-surface-card text-content-secondary hover:bg-surface-card-hover'
            }`}
          >
            Storefront
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={isPanelMode}
            onClick={() => setQrMode('panel')}
            className={`px-3 py-1.5 font-medium transition-colors ${
              isPanelMode
                ? 'bg-primary text-white'
                : 'bg-surface-card text-content-secondary hover:bg-surface-card-hover'
            }`}
          >
            Admin panel
          </button>
        </div>
      )}

      {/* Colour picker — 15 accent swatches + black + "match theme".
          Hidden for a custom upload, whose colours are baked into the
          uploaded image and can't be re-tinted client-side. Swatches
          rather than a <select> so the admin sees the actual ink colour
          before committing a print run. */}
      {!hasCustom && (
        <div className="mt-3">
          <div className="mb-1.5 text-[11px] uppercase tracking-wide text-content-muted">
            QR colour
          </div>
          <div role="radiogroup" aria-label="QR colour" className="flex flex-wrap gap-1.5">
            {QR_COLOR_OPTIONS.map((opt) => {
              const selected = colorPref === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={opt.label}
                  title={opt.label}
                  onClick={() => setColorPref(opt.value)}
                  className={`h-6 w-6 rounded-full border transition-shadow ${
                    selected
                      ? 'border-content-primary ring-2 ring-primary ring-offset-1 ring-offset-surface-card'
                      : 'border-border hover:border-border-strong'
                  }`}
                  style={{ backgroundColor: opt.swatch }}
                />
              );
            })}
          </div>
          {!contrastSafe && (
            <p className="mt-2 rounded border border-warning bg-warning-light px-2 py-1 text-[11px] text-warning">
              ⚠ This colour has only {contrastRatio(qrFgColor, '#ffffff').toFixed(1)}:1 contrast
              against white. Phone cameras may fail to scan it once printed — prefer a darker
              colour for labels that will be scanned in the field.
            </p>
          )}
        </div>
      )}

      <div className="mt-3 flex flex-col items-center gap-3 sm:flex-row sm:items-start sm:gap-6">
        <div
          ref={canvasWrapRef}
          className="rounded-md border border-border bg-white p-3"
          style={{ lineHeight: 0 }}
        >
          {hasCustom ? (
            // Uploaded custom QR — 256×256 to match the generated canvas
            // dimensions so the layout is stable when merchants toggle
            // between "use my upload" and "use auto-generated".
            <img
              src={customQrImageUrl!}
              alt="Custom product QR"
              width={256}
              height={256}
              style={{ width: 256, height: 256, objectFit: 'contain', display: 'block' }}
            />
          ) : (
            <QRCodeCanvas
              value={qrUrl}
              size={256}
              level="M"
              includeMargin={false}
              bgColor="#ffffff"
              fgColor={qrFgColor}
            />
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-content-muted">Product</div>
            <div className="truncate text-sm font-semibold text-content-primary">
              {productName || '—'}
            </div>
          </div>
          {sku && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-content-muted">SKU</div>
              <div className="truncate font-mono text-xs text-content-secondary">{sku}</div>
            </div>
          )}
          <div>
            <div className="text-[11px] uppercase tracking-wide text-content-muted">
              {urlLabel}
            </div>
            <div className="break-all rounded border border-border bg-surface-card-hover px-2 py-1 font-mono text-[11px] text-content-secondary">
              {qrUrl}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button size="sm" variant="outline" onClick={handleDownload}>
              <Download className="h-3.5 w-3.5" /> Download PNG
            </Button>
          </div>
          <p className="text-[11px] text-content-muted">{helperText}</p>
          {localhostWarning && (
            <p className="mt-1 rounded border border-warning bg-warning-light px-2 py-1 text-[11px] text-warning">
              ⚠ {localhostWarning}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}
